import { v4 } from "uuid";
import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../../../lib/grpc";
import { ScenarioRepository } from "../../domain/interfaces/ScenarioRepository";
import { ScenarioEntity } from "../../domain/scenario.entity";
import { ScenarioMapper } from "../mappers/ScenarioMapper";
import { ScenarioAPIPromiseClient } from "../../../../../gen/witbooking/backoffice/orchestrator/scenario/v1/scenario_api_grpc_web_pb";
import {
  CreateScenarioRequest,
  DeleteScenarioRequest,
  GetAllScenariosRequest,
  GetScenarioRequest,
  ReorderScenarioRequest,
  UpdateScenarioRequest,
} from "../../../../../gen/witbooking/backoffice/orchestrator/scenario/v1/scenario_api_pb";

export class ScenarioGrpcRepository implements ScenarioRepository {
  private readonly client: ScenarioAPIPromiseClient;

  constructor() {
    this.client = buildGrpcClient(ScenarioAPIPromiseClient);
  }

  async findByProperty(propertyTicker: string): Promise<ScenarioEntity[]> {
    const request = new GetAllScenariosRequest();
    request.setPropertyTicker(propertyTicker);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.getAllScenarios);
    const response = await call(request, { "x-property": propertyTicker });

    return ScenarioMapper.toDomainArray(response.getScenariosList());
  }

  async findById(propertyTicker: string, id: string): Promise<ScenarioEntity | null> {
    const request = new GetScenarioRequest();
    request.setPropertyTicker(propertyTicker);
    request.setId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.getScenario);
    const response = await call(request, { "x-property": propertyTicker });

    const scenario = response.getScenario();
    return scenario ? ScenarioMapper.toDomain(scenario) : null;
  }

  async create(propertyTicker: string, entity: ScenarioEntity): Promise<ScenarioEntity> {
    const request = new CreateScenarioRequest();
    request.setPropertyTicker(propertyTicker);
    request.setScenario(ScenarioMapper.toProto(entity));

    const call = addAuthzMetadataGrpcCall(this.client, this.client.createScenario);
    const response = await call(request, { "x-property": propertyTicker });

    const scenario = response.getScenario();
    if (!scenario) {
      throw new Error("Unexpected response creating Scenario");
    }
    return ScenarioMapper.toDomain(scenario);
  }

  async update(propertyTicker: string, entity: ScenarioEntity): Promise<void> {
    const request = new UpdateScenarioRequest();
    request.setPropertyTicker(propertyTicker);
    request.setScenario(ScenarioMapper.toProto(entity));

    const call = addAuthzMetadataGrpcCall(this.client, this.client.updateScenario);
    await call(request, { "x-property": propertyTicker });
  }

  async delete(propertyTicker: string, id: string): Promise<void> {
    const request = new DeleteScenarioRequest();
    request.setPropertyTicker(propertyTicker);
    request.setId(id);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.deleteScenario);
    await call(request, { "x-property": propertyTicker });
  }

  async reorder(propertyTicker: string, id: string, after: string): Promise<void> {
    const request = new ReorderScenarioRequest();
    request.setPropertyTicker(propertyTicker);
    request.setId(id);
    request.setAfter(after);

    const call = addAuthzMetadataGrpcCall(this.client, this.client.reorderScenario);
    await call(request, { "x-property": propertyTicker });
  }

  async duplicate(propertyTicker: string, id: string): Promise<ScenarioEntity> {
    const existing = await this.findById(propertyTicker, id);
    if (!existing) {
      throw new Error(`Scenario with id ${id} not found`);
    }
    const duplicated: ScenarioEntity = {
      ...existing,
      id: v4(),
      ticker: `${existing.ticker}_copy`,
    };
    return this.create(propertyTicker, duplicated);
  }
}
